import React, { useState } from 'react';
import { Star, Send, CheckCircle, ExternalLink, MessageSquare } from 'lucide-react';
import contenu from '../contenu';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster, toast } from 'sonner';
import emailjs from '@emailjs/browser';

// ─── EmailJS config ───────────────────────────────────────────
const EMAILJS_SERVICE_ID    = 'service_36blu1h';      // ex: service_xxxxxxx
const EMAILJS_TEMPLATE_AVIS = 'template_lu13b2w';  // ex: template_xxxxxxx
const EMAILJS_PUBLIC_KEY    = 'pvcnz9LSDqlrZHNmB';      // ex: xxxxxxxxxxxxxxxxx
// ─────────────────────────────────────────────────────────────

const GOOGLE_REVIEW_URL = 'https://share.google/DIwNn63RPQYsNk82X';

// Les avis viennent de contenu.js — modifiez ce fichier pour ajouter des avis
const existingTestimonials = contenu.avis;

const Stars = ({ count, interactive = false, onSet }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i}
        className={`w-6 h-6 transition-transform ${interactive ? 'cursor-pointer hover:scale-110' : ''}`}
        fill={i <= count ? '#D4A017' : '#e5e7eb'}
        viewBox="0 0 20 20"
        onClick={() => interactive && onSet && onSet(i)}
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

const AvisPage = () => {
  const [form, setForm] = useState({ name: '', event: '', rating: 5, text: '' });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error('Merci de remplir votre nom et votre avis.');
      return;
    }
    setSending(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_AVIS,
        {
          reviewer_name:  form.name,
          reviewer_event: form.event || 'Non précisé',
          reviewer_rating: `${form.rating}/5`,
          reviewer_text:  form.text,
          year:           new Date().getFullYear(),
        },
        EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
      toast.success('Merci pour votre avis !');
    } catch (err) {
      console.error(err);
      toast.error('Une erreur est survenue. Réessayez ou contactez-nous directement.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* Hero */}
      <section
        className="pt-36 pb-20 text-center"
        style={{ background: 'linear-gradient(to bottom right, hsl(0,0%,12%), hsl(0,64%,20%), hsl(0,0%,12%))' }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-gold font-medium mb-2 uppercase tracking-widest text-sm">Témoignages</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-cream mb-4">Avis Clients</h1>
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <div className="flex items-center justify-center gap-2 mb-4">
            <Stars count={5} />
            <span className="text-cream ml-2 font-semibold">5/5 sur Google · Avis vérifiés</span>
          </div>
          <p className="text-cream/70 text-lg">La satisfaction de nos clients, c'est notre plus belle récompense.</p>
        </div>
      </section>

      {/* Google CTA */}
      <section className="bg-white border-b border-gold/20 py-6">
        <div className="max-w-3xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-charcoal/70 text-sm">
            Vous avez fait appel à nos services ? Laissez-nous un avis Google pour nous aider à grandir !
          </p>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all whitespace-nowrap shadow"
          >
            <ExternalLink className="w-4 h-4" />
            Laisser un avis Google
          </a>
        </div>
      </section>

      {/* Existing testimonials */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-charcoal mb-10 text-center">Ce qu'ils disent de nous</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {existingTestimonials.map((t, i) => (
              <div key={i} className="bg-white border border-gold/20 rounded-2xl p-8 flex flex-col gap-4 shadow-sm hover:shadow-md transition-shadow">
                <Stars count={t.rating} />
                <p className="text-charcoal/80 leading-relaxed italic flex-1">"{t.text}"</p>
                <div className="pt-4 border-t border-gold/20">
                  <p className="text-charcoal font-bold">{t.name}</p>
                  <p className="text-charcoal/50 text-sm">{t.event} · {t.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit review form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 rounded-full mb-4">
              <MessageSquare className="w-7 h-7 text-gold" />
            </div>
            <h2 className="text-3xl font-bold text-charcoal mb-2">Partagez votre expérience</h2>
            <div className="w-12 h-1 bg-gold mx-auto mb-4" />
            <p className="text-charcoal/60">Votre avis nous aide à nous améliorer et rassure les futurs clients.</p>
          </div>

          {submitted ? (
            <div className="text-center py-16">
              <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-charcoal mb-2">Merci pour votre avis !</h3>
              <p className="text-charcoal/60">Il sera publié après vérification. Nous apprécions votre confiance.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-cream rounded-2xl p-8 shadow-sm space-y-6">
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">Votre nom *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Marie Dupont"
                  className="w-full px-4 py-3 rounded-lg border border-gold/30 bg-white focus:outline-none focus:ring-2 focus:ring-gold/40 text-charcoal"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">Type d'événement</label>
                <input
                  type="text"
                  value={form.event}
                  onChange={e => setForm({ ...form, event: e.target.value })}
                  placeholder="Anniversaire, Mariage, Repas d'entreprise…"
                  className="w-full px-4 py-3 rounded-lg border border-gold/30 bg-white focus:outline-none focus:ring-2 focus:ring-gold/40 text-charcoal"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-3">Votre note *</label>
                <Stars count={form.rating} interactive onSet={(r) => setForm({ ...form, rating: r })} />
                <p className="text-xs text-charcoal/40 mt-1">{form.rating} étoile{form.rating > 1 ? 's' : ''} sur 5</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">Votre avis *</label>
                <textarea
                  value={form.text}
                  onChange={e => setForm({ ...form, text: e.target.value })}
                  placeholder="Décrivez votre expérience en quelques mots…"
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gold/30 bg-white focus:outline-none focus:ring-2 focus:ring-gold/40 text-charcoal resize-none"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all shadow-md hover:shadow-lg disabled:opacity-60"
              >
                {sending ? (
                  <span>Envoi en cours…</span>
                ) : (
                  <><Send className="w-4 h-4" />Envoyer mon avis</>
                )}
              </button>
              <p className="text-xs text-center text-charcoal/40">Les avis sont publiés après modération.</p>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default AvisPage;
